/**
 * Formatting Utilities for Summary Statistics
 * Pure functions for formatting currency, numbers, and percentages
 */

/**
 * Format currency value with M/K notation
 * @param {number|string} value - Value to format
 * @returns {string} Formatted currency string (e.g., "$14.1 M", "$5.2 K", "$500")
 */
export function formatCurrency(value) {
  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) return '$0'

  // Abbreviate by MAGNITUDE. The thresholds used to be `>=`, so no negative
  // ever abbreviated and a saving arrived as "$-2,500,000" — twelve characters
  // in a 160px card, next to siblings reading "$8.6 M".
  const sign = numValue < 0 ? '-' : ''
  const magnitude = Math.abs(numValue)

  if (magnitude >= 1000000) {
    return sign + '$' + (magnitude / 1000000).toFixed(1) + ' M'
  } else if (magnitude >= 1000) {
    return sign + '$' + (magnitude / 1000).toFixed(1) + ' K'
  } else {
    return sign + '$' + Math.round(magnitude).toLocaleString('en-US')
  }
}

/**
 * Format large numbers with M/K notation (same logic as currency but no $)
 * @param {number|string} value - Value to format
 * @returns {string} Formatted number string (e.g., "14.1 M", "5.2 K", "500")
 */
export function formatCompactNumber(value) {
  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) return '0'

  // By magnitude, as formatCurrency above.
  const sign = numValue < 0 ? '-' : ''
  const magnitude = Math.abs(numValue)

  if (magnitude >= 1000000) {
    return sign + (magnitude / 1000000).toFixed(1) + ' M'
  } else if (magnitude >= 1000) {
    return sign + (magnitude / 1000).toFixed(1) + ' K'
  } else {
    return sign + Math.round(magnitude).toLocaleString('en-US')
  }
}

/**
 * Format breakdown value based on card type
 * @param {number|string} value - Value to format
 * @param {string} cardType - Type of card ('currency', 'percent', 'number')
 * @returns {string} Formatted value string
 */
export function formatBreakdownValue(value, cardType) {
  if (!value && value !== 0) return '—'
  
  const numValue = typeof value === 'number' ? value : parseFloat(value)
  if (isNaN(numValue)) return '—'
  
  switch (cardType) {
    case 'currency':
      return formatCurrency(numValue)
    case 'percent':
      return numValue.toFixed(1) + '%'
    case 'number':
      return Math.round(numValue).toLocaleString('en-US')
    default:
      return value.toString()
  }
}

















